'use client';

import * as React from 'react';
import { toast } from 'sonner';

import { cn } from 'lib/utils';

interface CopyIdProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  /** Full identifier copied to the clipboard. */
  value: string;
  /** Optional truncated label. Defaults to a head/tail truncation of `value`. */
  display?: React.ReactNode;
  /** Truncate after this many leading + trailing chars. Default 12 (head only). */
  truncateAt?: number;
  /** Toast message when copied. Pass `null` to suppress. Default "Copied to clipboard". */
  toastMessage?: string | null;
}

const truncate = (value: string, max: number) =>
  value.length <= max ? value : `${value.slice(0, max)}…`;

const CopyId = React.forwardRef<HTMLButtonElement, CopyIdProps>(
  (
    {
      value,
      display,
      truncateAt = 12,
      toastMessage = 'Copied to clipboard',
      className,
      onClick,
      ...props
    },
    ref,
  ) => {
    const [copied, setCopied] = React.useState(false);
    const timer = React.useRef<ReturnType<typeof setTimeout>>();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (e.defaultPrevented) {
        return;
      }
      navigator.clipboard.writeText(value).catch(() => {
        toast.error('Could not copy — clipboard access blocked');
      });
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1200);
      if (toastMessage) {
        toast.success(toastMessage);
      }
    };

    React.useEffect(() => () => clearTimeout(timer.current), []);

    return (
      <button
        ref={ref}
        type="button"
        title={`Copy ${value}`}
        aria-label={`Copy ${value}`}
        onClick={handleClick}
        className={cn(
          '-ml-1.5 inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent px-1.5 py-[3px] font-mono text-xs transition-colors',
          copied
            ? 'text-[var(--rye-green)]'
            : 'text-[var(--text-muted)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]',
          className,
        )}
        {...props}
      >
        {display ?? truncate(value, truncateAt)}
        {copied ? (
          <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3 w-3">
            <path
              d="M2 6.5l3 3 5-6"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
            className="h-3 w-3 opacity-60 [button:hover_>_&]:opacity-100"
          >
            <rect x="3.5" y="3.5" width="6" height="6" stroke="currentColor" />
            <rect
              x="1.5"
              y="1.5"
              width="6"
              height="6"
              stroke="currentColor"
              fill="var(--surface)"
            />
          </svg>
        )}
      </button>
    );
  },
);
CopyId.displayName = 'CopyId';

export { CopyId };
